import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

export interface FilterCriteria {
  make?: string;
  model?: string;
  yearMin?: number;
  yearMax?: number;
  priceMin?: number;
  priceMax?: number;
  mileageMin?: number;
  mileageMax?: number;
  status?: string;
  location?: string;
  daysOnLotMin?: number;
  daysOnLotMax?: number;
  profitMin?: number;
  profitMax?: number;
}

interface AdvancedFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterCriteria) => void;
  onClearFilters: () => void;
  currentFilters: FilterCriteria;
  availableMakes: string[];
  availableModels: string[];
  availableStatuses: string[];
  availableLocations: string[];
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  currentFilters,
  availableMakes,
  availableModels,
  availableStatuses,
  availableLocations,
}) => {
  const [filters, setFilters] = useState<FilterCriteria>(currentFilters);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const handleFilterChange = (key: keyof FilterCriteria, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClearFilters = () => {
    const emptyFilters: FilterCriteria = {};
    setFilters(emptyFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined && value !== '' && value !== null
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FunnelIcon className="h-6 w-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">Advanced Filters</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Make Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
              <select
                value={filters.make || ''}
                onChange={(e) => handleFilterChange('make', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Makes</option>
                {availableMakes.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>

            {/* Model Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <select
                value={filters.model || ''}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Models</option>
                {availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {availableStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <select
                value={filters.location || ''}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Locations</option>
                {availableLocations.map((location) => (
                  <option key={location} value={location}>
                    {location.charAt(0).toUpperCase() + location.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.yearMin || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'yearMin',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1990"
                  max="2025"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.yearMax || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'yearMax',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1990"
                  max="2025"
                />
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Range ($)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.priceMin || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'priceMin',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.priceMax || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'priceMax',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>

            {/* Mileage Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mileage Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.mileageMin || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'mileageMin',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.mileageMax || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'mileageMax',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>

            {/* Days on Lot Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days on Lot</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.daysOnLotMin || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'daysOnLotMin',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.daysOnLotMax || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'daysOnLotMax',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>

            {/* Profit Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profit Range ($)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.profitMin || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'profitMin',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.profitMax || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'profitMax',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Preset Filters */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Filters</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const presetFilters: FilterCriteria = {
                    status: 'available',
                    daysOnLotMax: 30,
                  };
                  setFilters(presetFilters);
                }}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
              >
                Hot Inventory (≤30 days)
              </button>
              <button
                onClick={() => {
                  const presetFilters: FilterCriteria = {
                    status: 'available',
                    daysOnLotMin: 90,
                  };
                  setFilters(presetFilters);
                }}
                className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200"
              >
                Aged Inventory (≥90 days)
              </button>
              <button
                onClick={() => {
                  const presetFilters: FilterCriteria = {
                    priceMin: 0,
                    priceMax: 10000,
                  };
                  setFilters(presetFilters);
                }}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full hover:bg-green-200"
              >
                Under $10K
              </button>
              <button
                onClick={() => {
                  const presetFilters: FilterCriteria = {
                    profitMin: 2000,
                  };
                  setFilters(presetFilters);
                }}
                className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
              >
                High Profit (≥$2K)
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center">
            {hasActiveFilters && (
              <span className="text-sm text-gray-600 mr-4">
                {
                  Object.values(filters).filter((v) => v !== undefined && v !== '' && v !== null)
                    .length
                }{' '}
                active filters
              </span>
            )}
            <button
              onClick={handleClearFilters}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;
